/**
 * A one-page summary of a run, as a standalone HTML file.
 *
 * This is the artifact for the people who never open the app: a lead deciding
 * whether to act, or the pipeline team receiving a hand-back. Everything they
 * need is in one file they can open, print, or attach to a ticket.
 *
 * **No scripts, and no external references.** The CSS is inline, there are no
 * images, no fonts and no links to anything that must load. A summary that
 * needs the network to render is a summary that is blank when someone opens it
 * from an email attachment on a locked-down machine, which is exactly the
 * reader this exists for. It also means the file is inert: nothing in it can
 * execute, wherever it is opened.
 *
 * **Everything interpolated is escaped.** This is the one artifact that renders
 * scraped values as markup rather than as data — record titles, field values
 * and decision reasons all come from an upstream nobody here controls. An
 * unescaped `<` in a bid title would corrupt the document at best; a crafted
 * value would inject markup into a file a colleague opens. Every value goes
 * through `escapeHtml`, and the tests assert that rather than assuming it.
 */

import type { QaReport } from "./qa";
import type { RecoveryDecision } from "./decisions";
import { buildFileName, type ExportArtifact, type InputFileHash } from "./export";
import type { SourceProfile } from "./adapter-types";

export type ReviewSummaryInput = {
  profile: SourceProfile;
  /** Human name for the source, when the registry has one. */
  displayName?: string;
  profileVersion: number;
  policyHash: string | null;
  generatedAt: string;
  qa: QaReport;
  inputHashes: InputFileHash[];
  decisions: RecoveryDecision[];
  /**
   * The run's headline verdict, as the DRIFT analysis recorded it.
   *
   * Null when the caller does not have it. It is deliberately not derived from
   * the export gate: the gate answers whether a recovered artifact may be
   * written, which is a different question, and deriving one from the other
   * reported "Pass" for a quarantined run — the worst possible error in the one
   * line a lead actually reads.
   */
  verdict: "Pass" | "Warning" | "Quarantined" | null;
  /** The duplicate-title alert as it stood, when the profile configures one. */
  alert?: { outcome: string; headline: string } | null;
  /** Hashes of the artifacts this summary accompanies. */
  artifacts?: Array<{ fileName: string; sha256: string | null }>;
  /** Build stamp, so a report can be tied to the exact code that made it. */
  appVersion?: string;
  appCommit?: string;
};

/**
 * Escape for HTML text and quoted attribute content alike.
 *
 * Ampersand first, or the escapes this function itself introduces get escaped
 * again. Both quote characters are covered so the same function is safe in an
 * attribute, which is the mistake worth designing out rather than remembering.
 */
export function escapeHtml(value: unknown): string {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Findings worth putting in front of someone who will not open the app. */
export function topFindings(qa: QaReport, limit = 10): Array<{ category: string; severity: string; count: number }> {
  const groups = new Map<string, { category: string; severity: string; count: number }>();
  for (const finding of qa.findings) {
    const key = `${finding.category}|${finding.severity}`;
    const existing = groups.get(key);
    if (existing) {
      existing.count++;
      continue;
    }
    groups.set(key, { category: finding.category, severity: finding.severity, count: 1 });
  }
  const order: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
  return [...groups.values()]
    .sort((left, right) => {
      const bySeverity = (order[left.severity] ?? 9) - (order[right.severity] ?? 9);
      return bySeverity !== 0 ? bySeverity : right.count - left.count;
    })
    .slice(0, limit);
}

/** Decisions grouped by field, because that is how a reviewer reads them. */
export function decisionsByField(
  decisions: RecoveryDecision[]
): Array<{ field: string; count: number; reasons: string[] }> {
  const byField = new Map<string, { field: string; count: number; reasons: string[] }>();
  for (const decision of decisions) {
    const entry = byField.get(decision.field) ?? { field: decision.field, count: 0, reasons: [] };
    entry.count++;
    const reason = decision.reason.trim();
    // Distinct reasons only: forty cells decided with one sentence should read
    // as one sentence, and a field where the reasons DIFFER is the interesting
    // case this makes visible.
    if (reason.length > 0 && !entry.reasons.includes(reason)) entry.reasons.push(reason);
    byField.set(decision.field, entry);
  }
  return [...byField.values()].sort((left, right) => right.count - left.count || left.field.localeCompare(right.field));
}

const STYLES = `
  :root { color-scheme: light }
  body { margin: 0; padding: 24px; font: 14px/1.5 system-ui, -apple-system, "Segoe UI", sans-serif; color: #0f172a; background: #f8fafc }
  main { max-width: 52rem; margin: 0 auto }
  h1 { font-size: 20px; margin: 0 0 4px }
  h2 { font-size: 15px; margin: 24px 0 8px }
  p, li { margin: 4px 0 }
  .muted { color: #475569; font-size: 12px }
  .verdict { display: inline-block; border: 1px solid; border-radius: 4px; padding: 2px 8px; font-weight: 600 }
  .pass { border-color: #86efac; background: #f0fdf4; color: #166534 }
  .warning { border-color: #fcd34d; background: #fffbeb; color: #92400e }
  .quarantined { border-color: #fca5a5; background: #fef2f2; color: #991b1b }
  table { width: 100%; border-collapse: collapse; margin-top: 8px }
  th, td { border-bottom: 1px solid #e2e8f0; padding: 6px 8px; text-align: left; vertical-align: top }
  th { font-size: 12px; text-transform: uppercase; letter-spacing: .04em; color: #475569 }
  code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; background: #f1f5f9; padding: 1px 4px; border-radius: 3px }
  .note { border: 1px solid #e2e8f0; background: #fff; border-radius: 6px; padding: 12px; margin-top: 8px }
`;

export function buildReviewSummaryHtml(input: ReviewSummaryInput): string {
  const e = escapeHtml;
  const findings = topFindings(input.qa);
  const fields = decisionsByField(input.decisions);
  const actors = [...new Set(input.decisions.map((decision) => decision.actor))].sort();
  const decidedAt = input.decisions.map((decision) => decision.timestamp).sort();

  const rows = (cells: string[][]) =>
    cells.map((row) => `<tr>${row.map((cell) => `<td>${cell}</td>`).join("")}</tr>`).join("\n        ");

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>Review summary — ${e(input.profile.id)}</title>
<style>${STYLES}</style>
</head>
<body>
<main>
  <h1>Review summary</h1>
  <p class="muted">
    ${e(input.displayName ?? input.profile.id)} ·
    policy <code>${e(input.profile.id)}</code> v${e(input.profileVersion)} ·
    hash <code>${e(input.policyHash ?? "unstamped")}</code>
  </p>
  <p class="muted">Run ${e(input.generatedAt)}${
    input.appVersion ? ` · app v${e(input.appVersion)}` : ""
  }${input.appCommit ? ` · build <code>${e(input.appCommit)}</code>` : ""}</p>

  <h2>Verdict</h2>
  <p>${
    input.verdict === null
      ? `<span class="muted">Not recorded in this artifact set. The quality report carries it.</span>`
      : `<span class="verdict ${e(input.verdict.toLowerCase())}">${e(input.verdict)}</span>`
  }</p>
  <p class="muted">
    Match rate ${e(input.qa.matchReport.matchRate.toFixed(4))} against a required
    ${e(input.profile.minimumMatchRate)}.
    ${input.qa.matchReport.meetsMinimumMatchRate ? "Met." : "NOT met — record pairing is not trustworthy."}
  </p>

  <h2>Alert</h2>
  ${
    input.alert
      ? `<p>${e(input.alert.headline)} <span class="muted">(${e(input.alert.outcome)})</span></p>
  <p class="muted">No hold was released by this tool. Releasing one is a manual action in the pipeline.</p>`
      : `<p class="muted">This source's profile configures no duplicate-title alert, so none was checked.</p>`
  }

  <h2>Top findings</h2>
  ${
    findings.length === 0
      ? `<p class="muted">The quality engine raised nothing.</p>`
      : `<table>
        <thead><tr><th>Category</th><th>Severity</th><th>Count</th></tr></thead>
        <tbody>
        ${rows(findings.map((finding) => [e(finding.category), e(finding.severity), e(finding.count)]))}
        </tbody>
      </table>`
  }

  <h2>Decisions</h2>
  ${
    fields.length === 0
      ? `<p class="muted">No decisions were recorded for this run.</p>`
      : `<p class="muted">
        ${e(input.decisions.length)} decision(s) across ${e(fields.length)} field(s),
        by ${e(actors.join(" and "))},
        between ${e(decidedAt[0] ?? "—")} and ${e(decidedAt[decidedAt.length - 1] ?? "—")}.
      </p>
      <table>
        <thead><tr><th>Field</th><th>Decisions</th><th>Reasons given</th></tr></thead>
        <tbody>
        ${rows(
          fields.map((field) => [
            e(field.field),
            e(field.count),
            field.reasons.map((reason) => e(reason)).join("<br />") || `<span class="muted">—</span>`
          ])
        )}
        </tbody>
      </table>`
  }

  <h2>Input files</h2>
  <table>
    <thead><tr><th>Role</th><th>File</th><th>SHA-256</th></tr></thead>
    <tbody>
    ${rows(
      input.inputHashes.map((hash) => [
        e(hash.role),
        e(hash.fileName),
        hash.sha256 ? `<code>${e(hash.sha256)}</code>` : `<span class="muted">unavailable</span>`
      ])
    )}
    </tbody>
  </table>

  ${
    input.artifacts && input.artifacts.length > 0
      ? `<h2>Artifacts in this hand-back</h2>
  <table>
    <thead><tr><th>File</th><th>SHA-256</th></tr></thead>
    <tbody>
    ${rows(
      input.artifacts.map((artifact) => [
        e(artifact.fileName),
        // For artifacts, unlike input files, an absent hash is not a gap: the
        // delivery manifest hashes every file in the hand-back, and it is built
        // after this summary precisely so it can hash this one too.
        artifact.sha256
          ? `<code>${e(artifact.sha256)}</code>`
          : `<span class="muted">in the delivery manifest</span>`
      ])
    )}
    </tbody>
  </table>`
      : ""
  }

  <div class="note">
    <p><strong>What this summary is not.</strong></p>
    <p class="muted">
      It reports what two export files differ by, under one source's policy. It does not say why the
      upstream scraper behaved as it did, and a recovered export is a stopgap for triage — the remedy
      for systemic loss is fixing and re-running the scraper.
    </p>
  </div>
</main>
</body>
</html>
`;
}

export function buildReviewSummaryArtifact(input: ReviewSummaryInput): ExportArtifact {
  return {
    kind: "review-summary",
    fileName: buildFileName("review-summary", input.profile.id, input.generatedAt),
    contentType: "text/html;charset=utf-8",
    content: buildReviewSummaryHtml(input)
  };
}
